<template>
    <lsn-loader-link
        v-tippy
        :content="running ? null : tooltip"
        :label="label"
        :is-loading="running"
        @click.stop="onClick"
    />
</template>

<script setup>
import { ref } from 'vue';
import { useToast } from 'vue-toastification';
import LsnLoaderLink from '@/components/LsnLoaderLink.vue';
import LsnCancelableToast from '@/components/LsnCancelableToast.vue';
import { useI18n } from 'vue-i18n';

// ------------------------------------------------------------ COMPOSABLES

const toast = useToast();
const { t } = useI18n({ useScope: 'global' });

// ------------------------------------------------------------ PROPS

const props = defineProps({
    label:
    {
        type:     String,
        required: true,
    },

    timeout:
    {
        type:    Number,
        default: 5000,
    },

    runningLabel:
    {
        type:    [String, null],
        default: null,
    },

    cancelLabel:
    {
        type:    [String, null],
        default: null,
    },

    tooltip:
    {
        type:    [String, null],
        default: null,
    },
});

// ------------------------------------------------------------ EMITS

const emit = defineEmits(['cancel', 'submit']);

// ------------------------------------------------------------ DATA

const running = ref(false);
const toastID = ref(null);

// ------------------------------------------------------------ METHODS

async function onClick()
{
    if(running.value)
    {
        return;
    }

    running.value = true;
    toastID.value = toast.info({
        component: LsnCancelableToast,
        props:
        {
            runningLabel: props.runningLabel ?? `${t('common.running')}…`,
            cancelLabel:  props.cancelLabel ?? t('common.cancel'),
        },
        listeners: { cancel, submit },
    }, {
        closeOnClick:    false,
        closeButton:     false,
        hideCloseButton: true,
        draggable:       false,
        pauseOnHover:    true,
        timeout:         props.timeout,
        hideProgressBar: false,
    });
}

function cancel()
{
    running.value = false;

    if(toastID.value !== null)
    {
        toast.dismiss(toastID.value);
        toastID.value = null;
    }

    emit('cancel');
}

function submit()
{
    running.value = false;

    if(toastID.value !== null)
    {
        toast.dismiss(toastID.value);
        toastID.value = null;
    }

    emit('submit');
}
</script>
